import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { getTestimonials } from '../mockData';
import { trackSlideChange } from '../utils/analytics';

const TestimonialsCarousel = () => {
  const testimonials = getTestimonials();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || testimonials.length === 0) return;

    const interval = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, isAutoPlaying, testimonials.length]);

  const handleNext = () => {
    const newIndex = (currentIndex + 1) % testimonials.length;
    setCurrentIndex(newIndex);
    trackSlideChange('testimonials', newIndex);
  };

  const handlePrev = () => {
    const newIndex = currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    trackSlideChange('testimonials', newIndex);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    trackSlideChange('testimonials', index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  if (testimonials.length === 0) {
    return null;
  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="relative max-w-4xl mx-auto">
      {/* Main Testimonial Card */}
      <div
        className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 relative overflow-hidden"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#0056D2] opacity-5 rounded-full -mr-16 -mt-16" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#FF7A00] opacity-5 rounded-full -ml-12 -mb-12" />

        {/* Quote Icon */}
        <div className="text-[#0056D2] opacity-20 text-7xl font-serif absolute top-8 left-8">“</div>

        <div className="relative z-10">
          {/* Stars */}
          <div className="flex items-center justify-center space-x-1 mb-6">
            {[...Array(currentTestimonial.rating || 5)].map((_, i) => (
              <Star key={i} size={20} className="fill-[#FF7A00] text-[#FF7A00]" />
            ))}
          </div>

          {/* Review Text */}
          <p className="text-[#222B45] text-lg md:text-xl leading-relaxed text-center mb-8 min-h-[120px] flex items-center justify-center">
            {currentTestimonial.review}
          </p>

          {/* Author Info */}
          <div className="text-center">
            <h4 className="text-[#0A1F44] font-semibold text-lg">{currentTestimonial.name}</h4>
            <p className="text-[#0056D2] text-sm font-medium">{currentTestimonial.role}</p>
            <p className="text-gray-500 text-sm">{currentTestimonial.company}</p>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={handlePrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-16 bg-white hover:bg-[#0056D2] text-[#0056D2] hover:text-white rounded-full p-3 shadow-xl transition-all duration-200 hover:scale-110 group"
        aria-label="Previous testimonial"
      >
        <ChevronLeft size={24} />
      </button>

      <button
        onClick={handleNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-16 bg-white hover:bg-[#0056D2] text-[#0056D2] hover:text-white rounded-full p-3 shadow-xl transition-all duration-200 hover:scale-110 group"
        aria-label="Next testimonial"
      >
        <ChevronRight size={24} />
      </button>

      {/* Dots Navigation */}
      <div className="flex items-center justify-center space-x-2 mt-8">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex
                ? 'w-8 h-2 bg-[#0056D2]'
                : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialsCarousel;